import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import FormInput from '../components/inputs/FormInput'
import PasswordInput from '../components/inputs/PasswordInput'
import RoundedButton from '../components/buttons/RoundedButton'
import LoadingModal from '../components/modals/LoadingModal'
import { ToastContainer, toast } from 'react-toastify'
import login from '../assets/login.png'
import arrow from '../assets/arrow.png'
import strings from '../res/strings'
import 'react-toastify/dist/ReactToastify.css'

const CreateAccount = () => {
    const [email, setEmail] = useState('')
    const [emailError, setEmailError] = useState(null)

    const [password, setPassword] = useState('')
    const [passwordError, setPasswordError] = useState(null)

    const [name, setName] = useState('')
    const [nameError, setNameError] = useState(null)

    const [nif, setNif] = useState('')
    const [nifError, setNifError] = useState(null)

    const [address, setAddress] = useState('')
    const [addressError, setAddressError] = useState(null)

    const [zipCode, setZipCode] = useState('')
    const [zipCodeError, setZipCodeError] = useState(null)

    const [locality, setLocality] = useState('')
    const [localityError, setLocalityError] = useState(null)

    const [isLoading, setIsLoading] = useState(false)

    const navigate = useNavigate()
    
    // Handle changes in email input text
    const onChangeEmail = (emailText) => {
        setEmailError(null)
        setEmail(emailText.target.value)
    }

    // Handle changes in password input text
    const onChangePassword = (passwordText) => {
        setPasswordError(null)
        setPassword(passwordText.target.value)
    }

    // Handle changes in name input text
    const onChangeName = (nameText) => {
        setNameError(null)
        setName(nameText.target.value)
    }

    // Handle changes in NIF input text
    const onChangeNIF = (nifText) => {
        setNifError(null)
        setNif(nifText.target.value)
    }

    // Handle changes in address input text
    const onChangeAddress = (addressText) => {
        setAddressError(null)
        setAddress(addressText.target.value)
    }

    // Handle changes in zip code input text
    const onChangeZipCode = (zipCodeText) => {
        setZipCodeError(null)
        setZipCode(zipCodeText.target.value)
    }

    // Handle changes in locality input text
    const onChangeLocality = (localityText) => {
        setLocalityError(null)
        setLocality(localityText.target.value)
    }

    // Check if email is valid
    const isValidEmail = (email) => {
        const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
        return emailPattern.test(email)
    }

    // Check if NIF is valid
    const isValidNIF = (nif) => {
        return /^\d+$/.test(nif)
    }

    // Check if zip code is valid
    const isValidZipCode = (zipCode) => {
        const zipCodePattern = /^\d{4}-\d{3}$/
        return zipCodePattern.test(zipCode)
    }

    // Check if password is valid (Minimum 8 characters, at least 1 upper case and 1 number)
    const isValidPassword = (password) => {
        const regex = /^(?=.*[A-Z])(?=.*\d).{8,}$/
        return regex.test(password)
    }

    // Check if all fields are valid and registers the user
    const registerUser = () => {
        let hasErrors = false

        if (email.trim().length === 0) {
            setEmailError(strings.insertEmailError)
            hasErrors = true
        } else if (!isValidEmail(email)) {
            hasErrors = true
            setEmailError(strings.invalidEmail)
        }
    
        if (password.trim().length === 0) {
            hasErrors = true
            setPasswordError(strings.insertPasswordError)
        } else if(!isValidPassword(password)) {
            hasErrors = true
            setPasswordError(strings.invalidPassword)
        }
    
        if (name.trim().length === 0) {
            hasErrors = true
            setNameError(strings.insertNameError)
        }
    
        if (nif.trim().length === 0) {
            hasErrors = true
            setNifError(strings.insertNifError)
        } else if (!isValidNIF(nif)) {
            hasErrors = true
            setNifError(strings.invalidNif)
        } else if (nif.trim().length < 9) {
            hasErrors = true
            setNifError(strings.invalidNif)
        }
    
        if (address.trim().length === 0) {
            hasErrors = true
            setAddressError(strings.insertAddressError)
        }
    
        if (zipCode.trim().length === 0) {
            hasErrors = true
            setZipCodeError(strings.insertZipCodeError);
        } else if (!isValidZipCode(zipCode)) {
            hasErrors = true
            setZipCodeError(strings.invalidZipCode)
        }
    
        if (locality.trim().length === 0) {
            hasErrors = true
            setLocalityError(strings.insertLocalityError)
        }

        if (!hasErrors) {
            setIsLoading(true)

            const userData = {
                Email: email.toLowerCase(),
                Password: password,
                Name: name,
                NIF: nif,
                Address: address,
                PostalCode: zipCode,
                Locality: locality
            }

            // Create user
            fetch('/api/Client', {
                method:'POST',
                headers: { 
                  'Accept': 'application/json',
                  'Content-Type': 'application/json; charset=UTF-8'
                }, 
                mode: 'cors',
                body: JSON.stringify(userData)
            }).then(async response => {
                setTimeout(() => {
                    setIsLoading(false)

                    if (response.status === 200) {
                        navigate('/create-account-success')
                    } else if (response.status === 409) {
                        showErrorToast(strings.createAccountDataError)
                    } else {
                        showErrorToast(strings.createAccountError)
                    }
                  }, 1000)

            }).catch((error) => {
                setTimeout(() => {
                    setIsLoading(false)
                    showErrorToast(error)
                }, 1000)   
            })
        }
    }
    
    // Handle show error toast
    const showErrorToast = (error) => {
        toast.error(error, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
            theme: 'colored'
        })
    }

    return (
        <div className='flex flex-row'>
            <div className='flex flex-1'>
                <img className='min-h-screen object-cover' src={login} alt='Login Alagem Digital'/>
            </div>

            <div className='flex flex-col flex-1 mb-6'>

                <div className='flex flex-row items-center mt-16 px-[7.5rem] space-x-2'>
                    <Link to='/'>
                        <img className='h-5 w-5' src={arrow} alt='Voltar atrás'/>
                    </Link>
                    <p className='font-montserrat font-semibold text-accent'>{strings.regist}</p>
                </div>
        
                <div className='flex flex-1 flex-col mt-11 px-[7.5rem]'>

                    <label htmlFor='email' className='font-montserrat font-semibold'>{strings.email}</label>
                    <FormInput 
                        id='email' 
                        type='email'
                        value={email}
                        placeholder={strings.insertEmail}
                        onChange={onChangeEmail}
                        error={emailError}
                    />
                    <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{emailError ? emailError : ''}</p>
                   
                    <label htmlFor='password' className='font-montserrat font-semibold mt-9'>{strings.password}</label>
                    <PasswordInput
                        value={password}
                        placeholder={strings.insertPassword}
                        onChange={onChangePassword}
                        error={passwordError}
                    />
                    <p className='font-montserrat text-sm font-medium text-black-transparent mt-4'>{strings.passwordMessage}</p>
                    <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{passwordError ? passwordError : ''}</p>
                   
                    <label htmlFor='name' className='font-montserrat font-semibold mt-9'>{strings.name}</label>
                    <FormInput 
                        id='name' 
                        type='text'
                        value={name}
                        placeholder={strings.insertName}
                        onChange={onChangeName}
                        error={nameError}
                    />
                    <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{nameError ? nameError : ''}</p>
                    
                    <label htmlFor='nif' className='font-montserrat font-semibold mt-9'>{strings.nif}</label>
                    <FormInput 
                        id='nif' 
                        type='text'
                        value={nif}
                        placeholder={strings.insertNif}
                        onChange={onChangeNIF}
                        error={nifError}
                        maxLength={9}
                    />
                    <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{nifError ? nifError : ''}</p>

                    <label htmlFor='address' className='font-montserrat font-semibold mt-9'>{strings.address}</label>
                    <FormInput 
                        id='address' 
                        type='text'
                        value={address}
                        placeholder={strings.insertAddress}
                        onChange={onChangeAddress}
                        error={addressError}
                    />
                    <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{addressError ? addressError : ''}</p>

                    <label htmlFor='zipCode' className='font-montserrat font-semibold mt-9'>{strings.zipCode}</label>
                    <FormInput 
                        id='zipCode' 
                        type='text'
                        value={zipCode}
                        placeholder={strings.insertZipCode}
                        onChange={onChangeZipCode}
                        error={zipCodeError}
                    />
                    <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{zipCodeError ? zipCodeError : ''}</p>

                    <label htmlFor='locality' className='font-montserrat font-semibold mt-9'>{strings.locality}</label>
                    <FormInput 
                        id='locality' 
                        type='text'
                        value={locality}
                        placeholder={strings.insertLocality}
                        onChange={onChangeLocality}
                        error={localityError}
                    />
                    <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{localityError ? localityError : ''}</p>

                    <div className='mt-9 flex self-center w-60'>
                        <RoundedButton 
                            onClick={registerUser}
                            text={strings.register}
                            height={48}
                            bgColor='#007EB4'
                        />
                    </div>

                </div>     
            </div>

            { isLoading ?
                <LoadingModal />
            : null
            }

            <ToastContainer />
               
        </div>
    )
}

export default CreateAccount